import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
    Button,
    Form,
    Input,
    App,
    Card,
    Flex,
    Divider,
} from 'antd';
import "antd/dist/reset.css";
import { DETAIL_CARD_CLASS, DETAIL_FIELD_CLASS, DETAIL_TEXTAREA_CLASS } from "../../../shared/constants/detailLayout";
import { Rows4Icon } from "lucide-react";

function Row({ label, children }) {
    return (
        <div className="flex flex-col gap-2">
            <label className="eyebrow" htmlFor={`edit-strings-row-${label.toLowerCase().replace(/\s+/g, '-')}`}>{label}</label>
            <div className={DETAIL_FIELD_CLASS} id={`edit-strings-row-${label.toLowerCase().replace(/\s+/g, '-')}`}>{children}</div>
        </div>
    );
}

Row.propTypes = {
    label: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
};

export { Row as EditStringsRow };

const defaultGetEntityId = (entity) => entity?.id;
const defaultGetInitialExtraState = () => ({});
const defaultBuildPayload = ({ name, description }) => ({
    name: name.trim(),
    description: description.trim() || null,
});

export default function EditStrings({
    entity,
    projectId,
    versionId,
    onClose,
    onSaved,
    title,
    renameSystemName,
    patchEntity,
    getEntityId = defaultGetEntityId,
    saveErrorMessage = "Не удалось сохранить изменения",
    systemNamePlaceholder = "iron_ingot",
    namePlaceholder = "Iron Ingot",
    dialogSize = "md",
    getInitialExtraState = defaultGetInitialExtraState,
    buildPayload = defaultBuildPayload,
    renderExtraFields,
}) {
    const entityId = getEntityId(entity);
    const [systemName, setSystemName] = useState("");
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [extraState, setExtraState] = useState(() => getInitialExtraState(entity));

    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        setSystemName(entity.systemName || "");
        setName(entity.name || "");
        setDescription(entity.description || "");
        setExtraState(getInitialExtraState(entity));
        setError("");
    }, [entity, getInitialExtraState]);

    const canSubmit = useMemo(
        () => !!entityId && systemName.trim() && name.trim() && !saving,
        [entityId, systemName, name, saving],
    );

    const onSubmit = async (e) => {
        e?.preventDefault?.();
        if (!canSubmit) return;

        setSaving(true);
        setError("");

        try {
            let current = entity;
            if (renameSystemName && systemName.trim() !== (entity.systemName || "")) {
                current = await renameSystemName(projectId, versionId, entityId, systemName.trim(), entity);
            }

            const payload = buildPayload({
                entity,
                entityId,
                systemName,
                name,
                description,
                extraState,
            });

            const updated = await patchEntity(projectId, versionId, entityId, payload, entity);

            onSaved?.(updated || current);
            onClose?.();
        } catch (e) {
            setError(e?.message || saveErrorMessage);
        } finally {
            setSaving(false);
        }
    };

    const setExtraField = (field, value) => {
        setExtraState((current) => ({
            ...current,
            [field]: value,
        }));
    };


    return (
        <App>
            <Card title={title} className={DETAIL_CARD_CLASS}>
                {error && (
                    <div className="mb-4 rounded-xl border border-red-300 bg-red-50 text-red-700 p-3 text-sm">
                        {error}
                    </div>
                )}

                <Form layout='vertical' onSubmit={onSubmit}>
                    <Form.Item label="" className="mb-1">
                        <Row label="systemName">
                            <Input
                                id={`edit-strings-row-systemname`}
                                className={`input ${DETAIL_FIELD_CLASS}`}
                                value={systemName}
                                onChange={(e) => setSystemName(e.target.value)}
                                placeholder={systemNamePlaceholder}
                                required
                            />
                        </Row>
                    </Form.Item>

                    <Form.Item label="" className="mb-1">
                        <Row label="Название">
                            <Input
                                id={`edit-strings-row-name`}
                                className={`input ${DETAIL_FIELD_CLASS}`}
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder={namePlaceholder}
                                required
                            /></Row>
                    </Form.Item>

                    <Form.Item label="" className="mb-1">
                        <Row label="Описание">
                            <Input.TextArea
                                className={`input ${DETAIL_TEXTAREA_CLASS}`}
                                rows={3}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                id={`edit-strings-row-description`}
                                placeholder="Описание для UI"
                            />
                        </Row>
                    </Form.Item>

                    {renderExtraFields?.({
                        entity,
                        entityId,
                        extraState,
                        setExtraField,
                        saving,
                    })}
                </Form>
                <Divider className="my-2" />
                <Flex gap="small" wrap align="center" justify="flex-end">
                    <Button type="primary" htmlType="submit" onClick={onSubmit} loading={saving} disabled={!canSubmit}>
                        Сохранить
                    </Button>
                    <Button variant="outlined" color="default" onClick={onClose}>
                        Отмена
                    </Button>
                </Flex>
            </Card>
        </App>
    );
}


EditStrings.propTypes = {
    entity: PropTypes.object,
    projectId: PropTypes.string,
    versionId: PropTypes.string,
    onClose: PropTypes.func,
    onSaved: PropTypes.func,
    title: PropTypes.string.isRequired,
    renameSystemName: PropTypes.func,
    patchEntity: PropTypes.func.isRequired,
    getEntityId: PropTypes.func,
    saveErrorMessage: PropTypes.string,
    systemNamePlaceholder: PropTypes.string,
    namePlaceholder: PropTypes.string,
    dialogSize: PropTypes.string,
    getInitialExtraState: PropTypes.func,
    buildPayload: PropTypes.func,
    renderExtraFields: PropTypes.func,
};